import { z } from 'zod';
import { outcomeSchema } from './alerts';
import { severitySchema } from './shared/severity';
/*
  KIR-5075: AI Risk Map dashboard view (Risk Flow, Blast Radius, Agent Radar).

  Internal UI-only contract behind the `ai_risk_map` flag. Not published on
  /api/v1 (unreleased flag, UI-only aggregation).

  Every payload is aggregated server-side for the whole organization and is
  readable by owner / manager / support, the same role rules as the rest of
  the organization dashboard.

  Severity indexes used in the compact arrays below:
    0 = low, 1 = medium, 2 = high, 3 = critical, -1 (or 4 in flow paths) = none.
*/
export const RISK_MAP_SEVERITIES = [
    'low',
    'medium',
    'high',
    'critical',
];
export const riskMapTabSchema = z.enum(['flow', 'blast', 'radar']);
export const riskMapTimeRangeQuerySchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
});
/*
  What a node / element refers to in Kirin. The frontend turns a ref into a
  deep link (it owns the table ids and URL keys); the server only names the
  entity.
*/
export const riskMapRefSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('user'),
        userId: z.string(),
        email: z.string().nullable(),
    }),
    z.object({ kind: z.literal('platform'), clientType: z.string() }),
    z.object({
        kind: z.literal('project'),
        projectId: z.string(),
        slug: z.string().nullable(),
    }),
    // Alert-type family: the alert types it groups (Alerts `type` filter).
    z.object({ kind: z.literal('alertFamily'), types: z.array(z.string()) }),
    z.object({ kind: z.literal('outcome'), outcome: outcomeSchema }),
    z.object({ kind: z.literal('severity'), severity: severitySchema }),
    z.object({ kind: z.literal('mcp'), name: z.string() }),
    z.object({ kind: z.literal('plugin'), name: z.string() }),
    z.object({ kind: z.literal('extension'), name: z.string() }),
    z.object({ kind: z.literal('hook'), name: z.string() }),
    // `category: 'skill'` marks a skill (AI Instructions hides skills while the
    // Skills inventory owns them, so the client may drop the link).
    z.object({
        kind: z.literal('instruction'),
        name: z.string(),
        category: z.enum(['skill']).optional(),
    }),
    z.object({
        kind: z.literal('shadowTool'),
        toolId: z.string(),
        name: z.string(),
        // Which Shadow AI table lists it: installed tools or browser AI tools.
        source: z.enum(['installed', 'browser']),
    }),
    z.object({ kind: z.literal('policy'), policyKey: z.string() }),
    // A single grouped alert (Alerts row expansion).
    // Optional type / user / platform narrow the Alerts table so the expanded
    // row is on the first page even when the alert is old.
    z.object({
        kind: z.literal('alert'),
        findingKey: z.string(),
        type: z.string().optional(),
        userId: z.string().optional(),
        clientType: z.string().optional(),
    }),
]);
// ---------------------------------------------------------------- Risk Flow
export const riskFlowDatasetSchema = z.enum(['alerts', 'inventory']);
export const riskFlowQuerySchema = riskMapTimeRangeQuerySchema.extend({
    dataset: riskFlowDatasetSchema.default('alerts'),
});
export const riskFlowNodeKindSchema = z.enum([
    'person',
    'platform',
    'project',
    'config',
    'session',
    'family',
    'outcome',
    'sev',
]);
export const riskFlowNodeSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: riskFlowNodeKindSchema,
    // Absent for buckets with no single Kirin target ("Everyone else", ...).
    ref: riskMapRefSchema.optional(),
    // False when an Alerts link filtered by `ref` would not return the alerts
    // behind this bar (e.g. a project bar that includes path-attributed alerts
    // the Alerts project filter cannot match). Absent = the link is exact.
    alertsLink: z.boolean().optional(),
    // alerts dataset (KIR-5115): distinct alerts behind this bar per severity
    // (0 = low .. 3 = critical). Path weights split an alert evenly over its
    // users and platforms, so a bar's summed weight undercounts shared alerts.
    // For people, platforms, alert types, outcomes and severities this is the
    // count the Alerts page shows for the same filter and time range. A project
    // bar holds each alert once, under one project (see `alertsLink`).
    alertCounts: z.array(z.number().int().min(0)).length(4).optional(),
});
export const riskFlowColumnSchema = z.object({
    key: z.string(),
    name: z.string(),
    nodes: z.array(riskFlowNodeSchema),
    // 'weight' = the client orders this column's nodes by weight, largest first.
    sort: z.enum(['fixed', 'weight']).default('fixed'),
});
export const riskFlowPathSchema = z.object({
    // One node index per column, left to right.
    nodes: z.array(z.number().int().min(0)),
    // 0..3 severity index, 4 = no findings (inventory only).
    sev: z.number().int().min(0).max(4),
    // alerts dataset: alerts / occurrences. inventory: assets / installs.
    weight: z.number(),
    altWeight: z.number(),
});
/*
  "What is not the standard".
  alerts: a (column, node) -> (column2, node2) combination whose share is at
  least 2x the org-wide norm, with 10+ alerts behind it.
  inventory: an asset outside the org standard (not built-in and used by
  fewer than three people) that carries findings.
*/
export const riskFlowDeviationSchema = z.object({
    label: z.string(),
    detail: z.string(),
    column: z.number().int(),
    node: z.number().int(),
    column2: z.number().int(),
    node2: z.number().int(),
    ratio: z.number(),
    count: z.number(),
    ref: riskMapRefSchema.optional(),
});
export const riskFlowResponseSchema = z.object({
    dataset: riskFlowDatasetSchema,
    columns: z.array(riskFlowColumnSchema),
    paths: z.array(riskFlowPathSchema),
    nonStandard: z.array(riskFlowDeviationSchema),
    totals: z.object({
        weight: z.number(),
        altWeight: z.number(),
        // inventory: assets outside the org standard / all assets.
        nonStandard: z.number(),
        all: z.number(),
    }),
    // True when the alert set hit the server-side group cap, so totals cover
    // only the most recent alerts in the window.
    truncated: z.boolean(),
});
// ------------------------------------------------------------- Blast Radius
export const BLAST_NODE_TYPES = [
    'person',
    'platform',
    'mcp',
    'plugin',
    'ext',
    'project',
    'shadow',
];
export const blastNodeTypeSchema = z.enum(BLAST_NODE_TYPES);
// Bit flags on a node.
export const BLAST_NODE_FLAGS = {
    BUILT_IN: 1,
    UNVERIFIED_OR_UNOFFICIAL: 2,
    INACTIVE: 4,
};
export const blastNodeSchema = z.object({
    type: blastNodeTypeSchema,
    name: z.string(),
    // -1 = no findings, else 0..3 (worst severity).
    sev: z.number().int().min(-1).max(3),
    users: z.number().int().min(0),
    sub: z.string(),
    alerts: z.number().int().min(0),
    flags: z.number().int().min(0),
    ref: riskMapRefSchema.optional(),
});
export const blastRadiusResponseSchema = z.object({
    nodes: z.array(blastNodeSchema),
    // Flat undirected pairs: [a0, b0, a1, b1, ...] (node indexes).
    edges: z.array(z.number().int().min(0)),
    // One entry per alert: [sevIndex, projectNodeIndex or -1, ...personNodeIndexes].
    alerts: z.array(z.array(z.number().int())),
    truncated: z.boolean(),
});
// --------------------------------------------------------------- Agent Radar
export const radarEventStatusSchema = z.enum(['blocked', 'flagged', 'logged']);
export const radarEventSchema = z.object({
    // `<finding_key>@<lastSeen ISO>`: stable across polls until the alert
    // fires again (ref.findingKey is the grouped alert itself).
    id: z.string(),
    verb: z.string(),
    text: z.string(),
    status: radarEventStatusSchema,
    sev: z.number().int().min(0).max(3),
    when: z.string(),
    file: z.string(),
    project: z.string(),
    title: z.string(),
    who: z.string(),
    platform: z.string(),
    ref: riskMapRefSchema,
});
export const radarAgentSchema = z.object({
    person: z.string(),
    platform: z.string(),
    // Oldest -> newest.
    events: z.array(radarEventSchema),
    tabs: z.array(z.string()),
    project: z.string(),
    last: z.string(),
    total: z.number().int(),
    userRef: riskMapRefSchema.optional(),
});
export const radarInventoryStatusSchema = z.enum([
    'Allowed',
    'Official',
    'Blocked',
    'Removed',
    'Flagged',
    'Review',
    'Unverified',
    'Unlisted',
]);
export const radarInventoryItemSchema = z.object({
    kind: z.enum(['MCP server', 'Skill', 'Extension', 'Plugin']),
    icon: z.enum(['mcp', 'skill', 'ext', 'plugin']),
    name: z.string(),
    by: z.string(),
    status: radarInventoryStatusSchema,
    when: z.string().nullable(),
    ref: riskMapRefSchema,
});
export const radarPolicySchema = z.object({
    policyKey: z.string(),
    name: z.string(),
    mode: z.string(),
});
export const agentRadarResponseSchema = z.object({
    agents: z.array(radarAgentSchema),
    // Oldest -> newest.
    feed: z.array(radarEventSchema),
    inventory: z.array(radarInventoryItemSchema),
    invSummary: z.record(z.string(), z.number()),
    invTotal: z.number().int(),
    policies: z.array(radarPolicySchema),
    agentVersions: z.object({
        latest: z.string().nullable(),
        onLatest: z.number().int(),
        total: z.number().int(),
    }),
    generatedAt: z.string(),
});
//# sourceMappingURL=ai-risk-map.js.map